/**
 * Servicio para manejar fotos de perfil de usuario
 * Compatible con PWA y web, usando Capacitor Camera y Firebase Storage
 */

import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { storage } from '../config/firebase.config';
import { Storage } from '@capacitor/storage';
import { getDefaultAvatar } from '../utils/defaultAvatar';
import {
  optimizeImage,
  loadImage,
  validateImageFile,
  dataUrlToBlob,
  ImageOptimizationOptions
} from '../utils/imageOptimization';

export interface ProfilePhotoOptions {
  quality?: number;
  allowEditing?: boolean;
  resultType?: CameraResultType;
  optimizeImage?: boolean;
  targetSize?: { width: number; height: number };
}

export interface ProfilePhotoResult {
  success: boolean;
  photoUrl?: string;
  blob?: Blob;
  error?: string;
}

class ProfilePhotoService {
  private static instance: ProfilePhotoService;
  private readonly defaultOptions: Required<ProfilePhotoOptions> = {
    quality: 80,
    allowEditing: true,
    resultType: CameraResultType.DataUrl,
    optimizeImage: true,
    targetSize: { width: 500, height: 500 }
  };

  static getInstance(): ProfilePhotoService {
    if (!ProfilePhotoService.instance) {
      ProfilePhotoService.instance = new ProfilePhotoService();
    }
    return ProfilePhotoService.instance;
  }

  /**
   * Optimiza una imagen antes de subirla
   */
  async optimizeImageForUpload(
    imageDataUrl: string,
    options: ImageOptimizationOptions = {}
  ): Promise<ProfilePhotoResult> {
    try {
      const img = await loadImage(imageDataUrl);
      const result = await optimizeImage(img, {
        maxWidth: 500,
        maxHeight: 500,
        quality: 0.9,
        format: 'jpeg',
        ...options
      });

      if (result.success && result.dataUrl) {
        return {
          success: true,
          photoUrl: result.dataUrl,
          blob: result.blob
        };
      } else {
        return {
          success: false,
          error: result.error || 'Error al optimizar la imagen'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: `Error al procesar la imagen: ${error}`
      };
    }
  }

  /**
   * Procesa una imagen desde un archivo
   */
  async processImageFile(file: File): Promise<ProfilePhotoResult> {
    try {
      // Validar archivo
      const validation = validateImageFile(file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Convertir archivo a data URL
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsDataURL(file);
      });

      // Optimizar imagen
      return await this.optimizeImageForUpload(dataUrl);
    } catch (error) {
      return {
        success: false,
        error: `Error al procesar el archivo: ${error}`
      };
    }
  }

  /**
   * Procesa una imagen recortada desde un blob
   */
  async processCroppedImage(blob: Blob): Promise<ProfilePhotoResult> {
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Error al leer el blob'));
        reader.readAsDataURL(blob);
      });

      return {
        success: true,
        photoUrl: dataUrl,
        blob: blob
      };
    } catch (error) {
      return {
        success: false,
        error: `Error al procesar la imagen recortada: ${error}`
      };
    }
  }

  /**
   * Tomar foto con la cámara
   */
  async takePhoto(options?: ProfilePhotoOptions): Promise<ProfilePhotoResult> {
    try {
      const finalOptions = { ...this.defaultOptions, ...options };

      // En PWA, verificar si Camera plugin está disponible
      if (!Capacitor.isNativePlatform()) {
        // Para PWA, usar input file con capture es más confiable
        return {
          success: false,
          error: 'Use input file con capture para PWA'
        };
      }

      const photo = await Camera.getPhoto({
        quality: finalOptions.quality!,
        allowEditing: finalOptions.allowEditing!,
        resultType: CameraResultType.DataUrl, // Forzar DataUrl para consistencia
        source: CameraSource.Camera,
        width: 400,
        height: 400
      });

      return await this.processPhoto(photo);
    } catch (error) {
      console.error('Error al tomar foto:', error);
      return {
        success: false,
        error: 'Error al acceder a la cámara. Verifique los permisos.'
      };
    }
  }

  /**
   * Seleccionar foto de la galería
   */
  async selectFromGallery(options?: ProfilePhotoOptions): Promise<ProfilePhotoResult> {
    try {
      const finalOptions = { ...this.defaultOptions, ...options };
      
      const photo = await Camera.getPhoto({
        quality: finalOptions.quality!,
        allowEditing: finalOptions.allowEditing!,
        resultType: finalOptions.resultType!,
        source: CameraSource.Photos,
        width: 400,
        height: 400
      });

      return await this.processPhoto(photo);
    } catch (error) {
      console.error('Error al seleccionar foto:', error);
      return {
        success: false,
        error: 'Error al acceder a la galería. Verifique los permisos.'
      };
    }
  }

  /**
   * Procesar la foto y subirla a Firebase Storage
   */
  private async processPhoto(photo: Photo): Promise<ProfilePhotoResult> {
    try {
      // Verificar si tenemos dataUrl o webPath
      let dataUrl = photo.dataUrl;

      if (!dataUrl && photo.webPath) {
        // En algunos casos PWA, la imagen viene como webPath
        try {
          const response = await fetch(photo.webPath);
          const blob = await response.blob();
          dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Error al leer blob'));
            reader.readAsDataURL(blob);
          });
        } catch (webPathError) {
          console.error('Error processing webPath:', webPathError);
        }
      }

      if (!dataUrl) {
        return {
          success: false,
          error: 'No se pudo obtener los datos de la imagen'
        };
      }

      // Retornar dataUrl para procesamiento posterior (no subir directamente)
      return {
        success: true,
        photoUrl: dataUrl
      };
    } catch (error) {
      console.error('Error al procesar foto:', error);
      return {
        success: false,
        error: 'Error al procesar la imagen'
      };
    }
  }

  /**
   * Convertir dataUrl a Blob
   */
  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }

  /**
   * Subir imagen a Firebase Storage
   */
  private async uploadToFirebase(blob: Blob): Promise<string> {
    try {
      // Obtener datos del usuario para crear path único
      const userCed = (await Storage.get({ key: 'userced' })).value;
      const eventCode = (await Storage.get({ key: 'eventcode' })).value;
      
      if (!userCed || !eventCode) {
        throw new Error('Datos de usuario no encontrados');
      }

      // Crear nombre único para el archivo
      const timestamp = Date.now();
      const fileName = `profile_${userCed}_${eventCode}_${timestamp}.jpg`;
      const filePath = `profile-photos/${eventCode}/${fileName}`;

      // Crear referencia en Firebase Storage
      const storageRef = storage.ref(filePath);
      
      // Subir archivo
      const uploadTask = await storageRef.put(blob, {
        contentType: 'image/jpeg',
        customMetadata: {
          userCed: userCed,
          eventCode: eventCode,
          uploadedAt: new Date().toISOString()
        }
      });

      // Obtener URL de descarga
      const downloadUrl = await uploadTask.ref.getDownloadURL();
      
      console.log('Foto subida exitosamente:', downloadUrl);
      return downloadUrl;
    } catch (error) {
      console.error('Error al subir a Firebase:', error);
      throw new Error('Error al subir la imagen al servidor');
    }
  }

  /**
   * Eliminar foto anterior del usuario (opcional)
   */
  async deleteOldPhoto(photoUrl: string): Promise<boolean> {
    try {
      if (!photoUrl || !photoUrl.includes('firebase')) {
        return true; // No es una foto de Firebase, no hay nada que eliminar
      }

      const storageRef = storage.refFromURL(photoUrl);
      await storageRef.delete();
      
      console.log('Foto anterior eliminada:', photoUrl);
      return true;
    } catch (error) {
      console.error('Error al eliminar foto anterior:', error);
      // No lanzar error, ya que esto no debe impedir la actualización
      return false;
    }
  }

  /**
   * Verificar si el dispositivo tiene cámara disponible
   */
  async isCameraAvailable(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        // En plataformas nativas, verificar permisos
        const permissions = await Camera.checkPermissions();
        return permissions.camera === 'granted' || permissions.camera === 'prompt';
      } else {
        // En PWA/web, verificar soporte de input file con capture
        return this.supportsCameraCapture();
      }
    } catch (error) {
      console.error('Error al verificar disponibilidad de cámara:', error);
      return false;
    }
  }

  /**
   * Verificar si el navegador soporta captura de cámara
   */
  private supportsCameraCapture(): boolean {
    try {
      // Verificar si el navegador soporta input file con capture
      const input = document.createElement('input');
      return 'capture' in input &&
             navigator.mediaDevices &&
             typeof navigator.mediaDevices.getUserMedia === 'function';
    } catch {
      return false;
    }
  }

  /**
   * Solicitar permisos de cámara
   */
  async requestCameraPermissions(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        const permissions = await Camera.requestPermissions();
        return permissions.camera === 'granted';
      }
      return true; // En web no necesitamos solicitar permisos explícitamente
    } catch (error) {
      console.error('Error al solicitar permisos:', error);
      return false;
    }
  }

  /**
   * Obtener URL de foto de perfil por defecto
   */
  getDefaultProfilePhoto(): string {
    return getDefaultAvatar();
  }

  /**
   * Validar si una URL es una imagen válida
   */
  async validateImageUrl(url: string): Promise<boolean> {
    try {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
      });
    } catch {
      return false;
    }
  }
}

export default ProfilePhotoService;
